/* eslint-disable no-use-before-define */
import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import lodashGet from 'lodash/get';
import invert from 'lodash/invert';
import isNumber from 'lodash/isNumber';
import i18n from '../../utils/i18n';
import {
  locationsTreeNodesType,
  oxygenType,
  oxygenUserType,
  paginationV2PropType,
  templateCategoriesEntitiesMapType,
  templatesCollectionType,
  templatesQueryParamsType
} from '../../constants/proptype-validation';
import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';
import {NotificationLevels, NotificationTypes} from '@adsk/bim360-matrix-react-notification-manager/es/helpers/consts';

import A from '@adsk/bim360-matrix-react-a';
import AssigneeCell from '@adsk/bim360-matrix-react-assignee-cell';
import {Column} from '@adsk/bim360-matrix-react-base-table';
import EmptyState from '@adsk/bim360-matrix-react-empty-state';
import InlineLoader from '@adsk/bim360-matrix-react-inline-loader';
import Loader from '@adsk/bim360-matrix-react-loader';
import LocationPicker from '@adsk/bim360-matrix-react-location-picker';
import MatrixTable from '@adsk/bim360-matrix-react-matrix-table';
import TablePagination from '@adsk/bim360-matrix-react-table-pagination';
import Text from '@adsk/bim360-matrix-react-text';
import Toggle from '@adsk/bim360-matrix-react-toggle';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import OverflowTooltip from '@adsk/bim360-matrix-react-overflow-tooltip';
import notFoundSvg from '@adsk/bim360-matrix-react-assets/not-found.svg';
import deleteIcon from '@adsk/bim360-matrix-react-assets/trash.svg';
import editIcon from '@adsk/bim360-matrix-react-assets/edit-icon-outline.svg';
import iconBlock from '@adsk/bim360-matrix-react-assets/icon-block.svg';
import iconCheck from '@adsk/bim360-matrix-react-assets/icon-check.svg';
import iconCopy from '../../assets/images/icons/icon-copy.svg';
import {findProjectMemberInOxygenMappingById} from '../../utils/userHelpers';
import {toLocalLongDate} from '../../utils/date-helpers';
import {sortAlphabeticallyByTitle} from '../../utils/sortingHelpers';
import {getAttributeValueFromTemplate} from '../../utils/issueAttributes-helpers';
import {getLastPageOffset} from '../../utils/pagination-helpers';
import ProjectListTableStatusCell from '../project-view/ProjectListTableStatusCell';
import {TEMPLATE_FIELD_NAMES, TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES} from '../../constants/issue-templates';
import {
  COLLECTION_TYPES,
  PAGE_PARAMS,
  SORTING_PARAMS,
  TEMPLATES_SORT_PROPERTIES
} from '../../constants/issue-query-params';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import {doesTemplateCategoryAllowTemplateToToggleIsActive} from '../../utils/issue-template-helpers';
import {hasPermittedAttribute} from '../../utils/issues-helpers';

// Defining column names for table use.
const TEMPLATES_COLUMN_KEYS = {
  CATEGORY: 'CATEGORY',
  TITLE: 'TITLE',
  ISSUE_TYPE: 'ISSUE_TYPE',
  ISSUE_SUB_TYPE: 'ISSUE_SUB_TYPE',
  STATUS: 'STATUS',
  DESCRIPTION: 'DESCRIPTION',
  LBS_LOCATION: 'LBS_LOCATION',
  LOCATION_DETAILS: 'LOCATION_DETAILS',
  ASSIGNED_TO: 'ASSIGNED_TO',
  DUE_DATE: 'DUE_DATE',
  OWNER: 'OWNER',
  ROOT_CAUSE: 'ROOT_CAUSE',
  CREATED_BY: 'CREATED_BY',
  CREATED_AT: 'CREATED_AT',
  UPDATED_BY: 'UPDATED_BY',
  UPDATED_AT: 'UPDATED_AT',
  NUMBER_OF_ISSUES: 'NUMBER_OF_ISSUES',
  IS_ACTIVE: 'IS_ACTIVE',
  CUSTOM_ATTRIBUTES: 'CUSTOM_ATTRIBUTES',
  ACTIONS: 'ACTIONS'
};

const actionColumnProps = {
  key: TEMPLATES_COLUMN_KEYS.ACTIONS,
  title: '',
  width: 50,
  resizable: false,
  style: {
    paddingRight: '20px'
  },
  className: 'TemplatesTable__action-column'
};

const tableActionOptions = {
  EDIT: 'EDIT',
  DUPLICATE: 'DUPLICATE',
  SET_AS_ACTIVE: 'SET_AS_ACTIVE',
  SET_AS_INACTIVE: 'SET_AS_INACTIVE',
  DELETE: 'DELETE'
};

class TemplatesTable extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      columns: this.getDefaultColumns(props)
    };

    this.columnGroupKeys = [
      {
        key: TEMPLATES_COLUMN_GROUPS.STANDARD,
        title: i18n.t('templatesGrid.columnGroups.standard')
      },
      {
        key: TEMPLATES_COLUMN_GROUPS.CUSTOM_ATTRIBUTES,
        title: i18n.t('templatesGrid.columnGroups.custom')
      }
    ];

    this.rowProps = {
      tagName: A,
      href: '',
      target: '_blank'
    };

    const analyticsIdSuffix = props.analyticsId ? `__${props.analyticsId}` : '';
    this.storageKey = `issues-ui__templates-table__${props.projectId}${analyticsIdSuffix}`;
  }

  getDefaultColumns(props) {
    const {customAttributes} = props;
    let columns = [];

    TemplatesTable.ColumnsOrder.forEach(columnKey => {
      const hidden = TemplatesTable.HiddenColumns.includes(columnKey);
      if (columnKey === TEMPLATES_COLUMN_KEYS.CUSTOM_ATTRIBUTES) {
        columns = columns.concat(
          customAttributes.sort(sortAlphabeticallyByTitle).map(customAttribute => ({
            columnKey: customAttribute.id,
            hidden: true,
            isCustom: true
          }))
        );
      } else {
        columns.push({columnKey, hidden});
      }
    });

    return columns;
  }

  getTextCellValue(value) {
    return value || value === 0 ? value : '-';
  }

  textDataGetter = ({rowData, column}) => {
    const data = lodashGet(rowData, column.dataKey);
    return this.getTextCellValue(data);
  };

  dueDateTextDataGetter = ({column, rowData}) => {
    const daysTilDueDate = lodashGet(rowData, column.dataKey);
    let dueDateText;
    if (!isNullOrUndefined(daysTilDueDate) && isNumber(daysTilDueDate)) {
      if (daysTilDueDate === 1) {
        dueDateText = i18n.t('templateAttributes.dueDate.single');
      } else {
        dueDateText = i18n.t('templateAttributes.dueDate.multiple', {count: daysTilDueDate});
      }
    }
    return this.getTextCellValue(dueDateText);
  };

  userNameTextDataGetter = ({column, rowData}) => {
    const userId = lodashGet(rowData, column.dataKey);
    const user = findProjectMemberInOxygenMappingById(userId, this.props.oxygen);
    return this.getTextCellValue(lodashGet(user, 'name'));
  };

  rootCauseDataGetter = ({column, rowData}) => {
    const rootCauseId = lodashGet(rowData, column.dataKey);
    const rootCause = this.props.rootCauseEntitiesMap[rootCauseId];
    const rootCauseText = rootCause ? rootCause.title : '';
    return this.getTextCellValue(rootCauseText);
  };

  issueSubTypeDataGetter = ({column, rowData}) => {
    const subtypeId = lodashGet(rowData, column.dataKey);
    return this.getTextCellValue(lodashGet(this.props.issueTypeAdminEntitiesMap, [subtypeId, 'title']));
  };

  issueTypeDataGetter = ({rowData}) => {
    const subtypeId = lodashGet(rowData, TEMPLATES_COLUMN_DATA_KEYS[TEMPLATES_COLUMN_KEYS.ISSUE_SUB_TYPE]);
    const typeId = lodashGet(this.props.issueTypeAdminEntitiesMap, [subtypeId, 'parentId']);
    return this.getTextCellValue(lodashGet(this.props.issueTypeAdminEntitiesMap, [typeId, 'title']));
  };

  dateDataGetter({column, rowData}) {
    const data = lodashGet(rowData, column.dataKey);
    return data ? toLocalLongDate(data) : '-';
  }

  onRowClick = ({event, rowData}) => {
    // ctrl+click or cmd+click should have default behavior of anchor link
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    event.preventDefault();
    const {changeLocationAction} = this.props;
    changeLocationAction(`project/${TYPE_QUALITY_ISSUES}/templates/${rowData.id}`);
  };

  onColumnSort = ({column, order}) => {
    const sort = column.sortField;
    const newSort = order === MatrixTable.SortOrder.DESC ? `-${sort}` : sort;
    this.props.setQueryParamsAction(
      {[SORTING_PARAMS.SORT]: newSort, [PAGE_PARAMS.OFFSET]: 0},
      COLLECTION_TYPES.TEMPLATES
    );
  };

  getActionOptions = ({rowData}) => {
    const svgSize = '16px';
    const TOGGLE_IS_ACTIVE_ACTION_OPTION = rowData[TEMPLATE_FIELD_NAMES.IS_ACTIVE]
      ? {
        key: tableActionOptions.SET_AS_INACTIVE,
        label: i18n.t('templatesGrid.tableActions.setAsInactive'),
        svg: iconBlock,
        width: svgSize,
        height: svgSize
      }
      : {
        key: tableActionOptions.SET_AS_ACTIVE,
        label: i18n.t('templatesGrid.tableActions.setAsActive'),
        svg: iconCheck,
        width: svgSize,
        height: svgSize
      };

    return [
      {
        key: tableActionOptions.EDIT,
        label: i18n.t('templatesGrid.tableActions.edit'),
        svg: editIcon,
        width: svgSize,
        height: svgSize
      },
      {
        key: tableActionOptions.DUPLICATE,
        label: i18n.t('templatesGrid.tableActions.duplicate'),
        svg: iconCopy,
        width: svgSize,
        height: svgSize
      },
      ...(this.isCategoryActive(rowData) ? [TOGGLE_IS_ACTIVE_ACTION_OPTION] : []),
      {
        key: tableActionOptions.DELETE,
        label: i18n.t('templatesGrid.tableActions.delete'),
        svg: deleteIcon,
        width: svgSize,
        height: svgSize
      }
    ];
  };

  handleRowAction = ({key, rowData}) => {
    const {changeLocationAction, setDuplicateTemplateIdAction, setDeleteTemplateIdAction} = this.props;
    switch (key) {
      case tableActionOptions.EDIT:
        changeLocationAction(`project/${TYPE_QUALITY_ISSUES}/templates/${rowData.id}`);
        break;
      case tableActionOptions.DUPLICATE:
        setDuplicateTemplateIdAction(rowData.id);
        break;
      case tableActionOptions.SET_AS_ACTIVE:
        this.handleTemplateActiveChange(rowData, true);
        break;
      case tableActionOptions.SET_AS_INACTIVE:
        this.handleTemplateActiveChange(rowData, false);
        break;
      case tableActionOptions.DELETE:
        setDeleteTemplateIdAction(rowData.id);
        break;
      default:
        console.log(`Cannot handle template action: ${key}`);
    }
  };

  renderStatus = ({cellData, container}) => (
    <ProjectListTableStatusCell container={container} cellData={cellData} showFull />
  );

  renderLocation = ({cellData, container}) => {
    if (!cellData) {
      return '-';
    }
    if (this.props.isLocationsTreeLoading) {
      return <InlineLoader className="TemplatesTable__location-loader" />;
    }

    return (
      <LocationPicker
        locationApiPayload={this.props.locationsTreeNodes}
        loadingError={this.props.isLocationsLoadingError}
        selectedEventKey={cellData}
        readOnlyEmptyValuePlaceholder=""
        readOnlyValue
        popoverContainer={container}
      />
    );
  };

  renderAssignee = ({cellData, container}) => {
    const assignedTo = findProjectMemberInOxygenMappingById(cellData, this.props.oxygen);
    if (!assignedTo) {
      return '-';
    }

    return <AssigneeCell container={container} primaryAssignee={assignedTo} currentUser={this.props.currentUser} />;
  };

  handleTemplateActiveChange(rowData, isActive) {
    this.props.updateTemplateAction(
      rowData.id,
      {
        [TEMPLATE_FIELD_NAMES.IS_ACTIVE]: isActive
      },
      {
        onSuccess: () => this.handleActiveSaveSuccess(rowData, isActive),
        onError: () => this.handleActiveSaveError(rowData, isActive)
      }
    );
  }

  handleActiveSaveSuccess = (template, isActive) => {
    const params = {
      title: template[TEMPLATE_FIELD_NAMES.ISSUE_ATTRIBUTES][TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.TITLE]
    };
    const key = isActive
      ? 'templatesGrid.notifications.setAsActiveSuccess'
      : 'templatesGrid.notifications.setAsInactiveSuccess';
    this.props.showNotificationAction({
      type: NotificationTypes.TOAST,
      level: NotificationLevels.SUCCESS,
      message: <Text hasHTML>{i18n.t(key, params)}</Text>
    });
  };

  handleActiveSaveError = (template, isActive) => {
    const params = {
      title: template[TEMPLATE_FIELD_NAMES.ISSUE_ATTRIBUTES][TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.TITLE]
    };
    const key = isActive
      ? 'templatesGrid.notifications.setAsActiveError'
      : 'templatesGrid.notifications.setAsInactiveError';
    this.props.showNotificationAction({
      type: NotificationTypes.SNACKBAR,
      level: NotificationLevels.ERROR,
      message: <Text hasHTML>{i18n.t(key, params)}</Text>
    });
  };

  onToggleClick(e) {
    // Prevent opening template edit modal on toggle click.
    e.stopPropagation();
    e.preventDefault();
  }

  isCategoryActive = rowData =>
    doesTemplateCategoryAllowTemplateToToggleIsActive(rowData, this.props.templateCategoriesEntitiesMap);

  renderIsActive = ({cellData, rowData}) => {
    const disabled = !hasPermittedAttribute(rowData, TEMPLATE_FIELD_NAMES.IS_ACTIVE) || !this.isCategoryActive(rowData);
    return (
      <Toggle
        checked={cellData}
        onClick={this.onToggleClick}
        onChange={isActive => this.handleTemplateActiveChange(rowData, isActive)}
        disabled={disabled}
      />
    );
  };

  renderCategory = ({cellData: categoryId, column}) => {
    const {isTemplateCategoriesCollectionLoading} = column;
    if (isTemplateCategoriesCollectionLoading) {
      return <InlineLoader className="TemplatesTable__category--loading" />;
    }

    const templateCategory = this.props.templateCategoriesEntitiesMap[categoryId];
    return (
      <OverflowTooltip
        autoTipOnEllipsis
        place={Tooltip.Places.TOP}
        content={templateCategory ? templateCategory.title : i18n.t('templateAttributes.category.uncategorized')}
        theme={Tooltip.Themes.DARK}
      >
        <span className="TemplatesTable__category">
          {templateCategory ? templateCategory.title : i18n.t('templateAttributes.category.uncategorized')}
        </span>
      </OverflowTooltip>
    );
  };

  renderColumn = (columnKey, hidden) => {
    const sortField = TEMPLATES_SORTABLE_COLUMN_KEYS[columnKey];
    const basicProps = {
      key: TEMPLATES_COLUMN_KEYS[columnKey],
      dataKey: TEMPLATES_COLUMN_DATA_KEYS[columnKey],
      groupKey: TEMPLATES_COLUMN_GROUPS.STANDARD,
      resizable: true,
      minWidth: 60,
      hidden,
      sortable: !!sortField,
      sortField
    };

    switch (columnKey) {
      case TEMPLATES_COLUMN_KEYS.CATEGORY:
        return (
          <Column
            {...basicProps}
            width={160}
            title={i18n.t('templateAttributes.category.label')}
            className="TemplatesTable__category-column"
            cellRenderer={this.renderCategory}
            isTemplateCategoriesCollectionLoading={this.props.isTemplateCategoriesCollectionLoading}
          />
        );
      case TEMPLATES_COLUMN_KEYS.TITLE:
        return (
          <Column
            {...basicProps}
            width={170}
            title={i18n.t('issueAttributes.title.label')}
            className="TemplatesTable__title-column"
            dataGetter={this.textDataGetter}
            flexGrow={1}
          />
        );
      case TEMPLATES_COLUMN_KEYS.ISSUE_TYPE: {
        return (
          <Column
            {...basicProps}
            width={120}
            className="TemplatesTable__issue-type-column"
            title={i18n.t('issueAttributes.issueType.label')}
            dataGetter={this.issueTypeDataGetter}
          />
        );
      }
      case TEMPLATES_COLUMN_KEYS.ISSUE_SUB_TYPE: {
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.issueSubType.label')}
            className="TemplatesTable__issue-subtype-column"
            dataGetter={this.issueSubTypeDataGetter}
          />
        );
      }
      case TEMPLATES_COLUMN_KEYS.STATUS:
        return (
          <Column
            {...basicProps}
            width={160}
            title={i18n.t('templateAttributes.status.label')}
            cellRenderer={this.renderStatus}
          />
        );
      case TEMPLATES_COLUMN_KEYS.DESCRIPTION:
        return (
          <Column
            {...basicProps}
            width={150}
            title={i18n.t('templateAttributes.description.label')}
            dataGetter={this.textDataGetter}
          />
        );
      case TEMPLATES_COLUMN_KEYS.LBS_LOCATION:
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.lbsLocation.label')}
            cellRenderer={this.renderLocation}
          />
        );
      case TEMPLATES_COLUMN_KEYS.LOCATION_DETAILS:
        return (
          <Column
            {...basicProps}
            width={150}
            title={i18n.t('issueAttributes.locationDetails.label')}
            dataGetter={this.textDataGetter}
          />
        );
      case TEMPLATES_COLUMN_KEYS.ASSIGNED_TO:
        return (
          <Column
            {...basicProps}
            width={150}
            title={i18n.t('issueAttributes.assignedTo.label')}
            cellRenderer={this.renderAssignee}
          />
        );
      case TEMPLATES_COLUMN_KEYS.DUE_DATE:
        return (
          <Column
            {...basicProps}
            width={150}
            title={i18n.t('issueAttributes.dueDate.label')}
            dataGetter={this.dueDateTextDataGetter}
          />
        );
      case TEMPLATES_COLUMN_KEYS.OWNER:
        return (
          <Column
            {...basicProps}
            width={150}
            title={i18n.t('issueAttributes.owner.label')}
            dataGetter={this.userNameTextDataGetter}
          />
        );
      case TEMPLATES_COLUMN_KEYS.ROOT_CAUSE:
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.rootCause.label')}
            dataGetter={this.rootCauseDataGetter}
          />
        );
      case TEMPLATES_COLUMN_KEYS.CREATED_BY:
        return (
          <Column
            {...basicProps}
            width={150}
            title={i18n.t('issueAttributes.createdBy.label')}
            dataGetter={this.userNameTextDataGetter}
          />
        );
      case TEMPLATES_COLUMN_KEYS.CREATED_AT:
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.createdAt.label')}
            dataGetter={this.dateDataGetter}
          />
        );
      case TEMPLATES_COLUMN_KEYS.UPDATED_BY:
        return (
          <Column
            {...basicProps}
            width={150}
            title={i18n.t('issueAttributes.updatedBy.label')}
            dataGetter={this.userNameTextDataGetter}
          />
        );
      case TEMPLATES_COLUMN_KEYS.UPDATED_AT:
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('issueAttributes.updatedAt.label')}
            dataGetter={this.dateDataGetter}
          />
        );
      case TEMPLATES_COLUMN_KEYS.NUMBER_OF_ISSUES:
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('templateAttributes.numberOfIssues.label')}
            dataGetter={this.textDataGetter}
          />
        );
      case TEMPLATES_COLUMN_KEYS.IS_ACTIVE:
        return (
          <Column
            {...basicProps}
            width={120}
            title={i18n.t('templateAttributes.isActive.label')}
            className="TemplatesTable__is-active-column"
            cellRenderer={this.renderIsActive}
          />
        );
      default:
        return null;
    }
  };

  renderCustomAttributeColumn = (customAttributeId, hidden) => {
    const customAttribute = this.props.customAttributes.find(ca => ca.id === customAttributeId);
    const dataGetter = ({rowData}) => this.getTextCellValue(getAttributeValueFromTemplate(customAttribute, rowData));

    return (
      <Column
        key={customAttributeId}
        width={150}
        minWidth={60}
        hidden={hidden}
        resizable
        groupKey={TEMPLATES_COLUMN_GROUPS.CUSTOM_ATTRIBUTES}
        title={customAttribute.title}
        dataGetter={dataGetter}
      />
    );
  };

  renderColumns() {
    const columnsToRender = [];
    this.getDefaultColumns(this.props).forEach(({columnKey, hidden, isCustom}) => {
      const columnRenderer = isCustom ? this.renderCustomAttributeColumn : this.renderColumn;
      const renderedColumn = columnRenderer(columnKey, hidden);
      renderedColumn && columnsToRender.push(renderedColumn);
    });

    return columnsToRender;
  }

  renderEmptyStateNotFound() {
    return (
      <EmptyState
        icon={{
          children: notFoundSvg,
          height: '160px',
          width: '380px'
        }}
        text={i18n.t('templatesGrid.emptyState.notFound')}
      />
    );
  }

  getPaginationPageOffset(pageData) {
    switch (pageData.link) {
      case TablePagination.PageLinks.NEXT:
        return pageData.offset + pageData.limit;
      case TablePagination.PageLinks.PREVIOUS:
        return pageData.offset - pageData.limit;
      case TablePagination.PageLinks.LAST:
        return getLastPageOffset(pageData.total, pageData.limit);
      case TablePagination.PageLinks.FIRST:
      default:
        return 0;
    }
  }

  handlePageChange = pageData => {
    const offset = this.getPaginationPageOffset(pageData);
    this.props.setQueryParamsAction({[PAGE_PARAMS.OFFSET]: offset}, COLLECTION_TYPES.TEMPLATES);
  };

  isEmptyState() {
    const {tableData, loading} = this.props;
    return !loading && (!tableData || tableData.length === 0);
  }

  renderFooter = () => {
    const {pagination} = this.props;
    if (!pagination || this.isEmptyState()) {
      return null;
    }

    const paginationMeta = {
      limit: pagination.limit,
      offset: pagination.offset,
      total: pagination.totalResults
    };

    return (
      <TablePagination
        className="TemplatesTable__pagination"
        onPageChange={this.handlePageChange}
        customPaginationDescription={i18n.t('templatesGrid.pagination.description')}
        paginationCounterSeparatorLabel={i18n.t('templatesGrid.pagination.separatorLabel')}
        {...paginationMeta}
      />
    );
  };

  getRowClassName = ({rowData}) =>
    cx('TemplatesTable__row', {
      'TemplatesTable__row--highlighted': this.props.highlightRowIds.includes(rowData.id)
    });

  render() {
    const {loading, didFetchOnce, tableData, queryParams} = this.props;
    if (loading && !didFetchOnce) {
      return <Loader />;
    }
    let sortField = queryParams[SORTING_PARAMS.SORT];
    let isAscending = true;

    if (sortField.startsWith('-')) {
      sortField = sortField.substring(1);
      isAscending = false;
    }

    const sortBy = {
      order: isAscending ? MatrixTable.SortOrder.ASC : MatrixTable.SortOrder.DESC,
      key: TEMPLATES_SORTABLE_KEYS_TO_COLUMN_KEYS[sortField]
    };

    return (
      <MatrixTable
        fixed
        attachTestId
        showSettings
        autoGrow={tableData.length > 0}
        storageKey={this.storageKey}
        containerClassName="TemplatesTable"
        className="TemplatesTable__table"
        headerClassName="TemplatesTable__header"
        rowClassName={this.getRowClassName}
        data={tableData}
        emptyRenderer={this.renderEmptyStateNotFound}
        rowProps={this.rowProps}
        onRowClick={this.onRowClick}
        onColumnSort={this.onColumnSort}
        actionColumnProps={actionColumnProps}
        actionOptions={this.getActionOptions}
        onRowAction={this.handleRowAction}
        actionMode={MatrixTable.ActionMode.COLUMN}
        loading={loading}
        sortBy={sortBy}
        columnGroups={this.columnGroupKeys}
        footerRenderer={this.renderFooter}
        footerHeight={tableData.length > 0 ? 40 : 0}
      >
        {this.renderColumns()}
      </MatrixTable>
    );
  }
}
/* eslint-enable no-use-before-define */
const getIssueAttributePath = attrName => `${TEMPLATE_FIELD_NAMES.ISSUE_ATTRIBUTES}.${attrName}`;

const TEMPLATES_COLUMN_DATA_KEYS = {
  [TEMPLATES_COLUMN_KEYS.CATEGORY]: TEMPLATE_FIELD_NAMES.CATEGORY_ID,
  [TEMPLATES_COLUMN_KEYS.CREATED_BY]: TEMPLATE_FIELD_NAMES.CREATED_BY,
  [TEMPLATES_COLUMN_KEYS.CREATED_AT]: TEMPLATE_FIELD_NAMES.CREATED_AT,
  [TEMPLATES_COLUMN_KEYS.UPDATED_BY]: TEMPLATE_FIELD_NAMES.UPDATED_BY,
  [TEMPLATES_COLUMN_KEYS.UPDATED_AT]: TEMPLATE_FIELD_NAMES.UPDATED_AT,
  [TEMPLATES_COLUMN_KEYS.NUMBER_OF_ISSUES]: TEMPLATE_FIELD_NAMES.NUMBER_OF_ISSUES,
  [TEMPLATES_COLUMN_KEYS.IS_ACTIVE]: TEMPLATE_FIELD_NAMES.IS_ACTIVE,
  [TEMPLATES_COLUMN_KEYS.TITLE]: getIssueAttributePath(TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.TITLE),
  [TEMPLATES_COLUMN_KEYS.ISSUE_TYPE]: getIssueAttributePath(TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.TYPE_ID),
  [TEMPLATES_COLUMN_KEYS.ISSUE_SUB_TYPE]: getIssueAttributePath(TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.SUBTYPE_ID),
  [TEMPLATES_COLUMN_KEYS.STATUS]: getIssueAttributePath(TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.STATUS),
  [TEMPLATES_COLUMN_KEYS.DESCRIPTION]: getIssueAttributePath(TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DESCRIPTION),
  [TEMPLATES_COLUMN_KEYS.LBS_LOCATION]: getIssueAttributePath(TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.LOCATION_ID),
  [TEMPLATES_COLUMN_KEYS.LOCATION_DETAILS]: getIssueAttributePath(
    TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.LOCATION_DETAILS
  ),
  [TEMPLATES_COLUMN_KEYS.ASSIGNED_TO]: getIssueAttributePath(TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ASSIGNED_TO),
  [TEMPLATES_COLUMN_KEYS.DUE_DATE]: getIssueAttributePath(TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.DUE_DATE),
  [TEMPLATES_COLUMN_KEYS.OWNER]: getIssueAttributePath(TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.OWNER_ID),
  [TEMPLATES_COLUMN_KEYS.ROOT_CAUSE]: getIssueAttributePath(TEMPLATE_ISSUE_ATTRIBUTES_FIELD_NAMES.ROOT_CAUSE_ID)
};

const TEMPLATES_SORTABLE_COLUMN_KEYS = {
  [TEMPLATES_COLUMN_KEYS.CATEGORY]: TEMPLATES_SORT_PROPERTIES.CATEGORY,
  [TEMPLATES_COLUMN_KEYS.TITLE]: TEMPLATES_SORT_PROPERTIES.TITLE,
  // [TEMPLATES_COLUMN_KEYS.ISSUE_TYPE]: 'typeId',
  // [TEMPLATES_COLUMN_KEYS.ISSUE_SUB_TYPE]: 'issueSubtypeId',
  [TEMPLATES_COLUMN_KEYS.STATUS]: TEMPLATES_SORT_PROPERTIES.STATUS,
  [TEMPLATES_COLUMN_KEYS.DESCRIPTION]: TEMPLATES_SORT_PROPERTIES.DESCRIPTION,
  [TEMPLATES_COLUMN_KEYS.LOCATION_DETAILS]: TEMPLATES_SORT_PROPERTIES.LOCATION_DETAILS,
  [TEMPLATES_COLUMN_KEYS.DUE_DATE]: TEMPLATES_SORT_PROPERTIES.DUE_DATE,
  // [TEMPLATES_COLUMN_KEYS.ROOT_CAUSE]: 'rootCauseId',
  [TEMPLATES_COLUMN_KEYS.CREATED_AT]: TEMPLATES_SORT_PROPERTIES.CREATED_AT,
  [TEMPLATES_COLUMN_KEYS.UPDATED_AT]: TEMPLATES_SORT_PROPERTIES.UPDATED_AT,
  [TEMPLATES_COLUMN_KEYS.NUMBER_OF_ISSUES]: TEMPLATES_SORT_PROPERTIES.ISSUES_COUNT,
  [TEMPLATES_COLUMN_KEYS.IS_ACTIVE]: TEMPLATES_SORT_PROPERTIES.IS_ACTIVE
};

const TEMPLATES_SORTABLE_KEYS_TO_COLUMN_KEYS = invert(TEMPLATES_SORTABLE_COLUMN_KEYS);

TemplatesTable.ColumnsOrder = [
  TEMPLATES_COLUMN_KEYS.CATEGORY,
  TEMPLATES_COLUMN_KEYS.TITLE,
  TEMPLATES_COLUMN_KEYS.ISSUE_TYPE,
  TEMPLATES_COLUMN_KEYS.ISSUE_SUB_TYPE,
  TEMPLATES_COLUMN_KEYS.STATUS,
  TEMPLATES_COLUMN_KEYS.DESCRIPTION,
  TEMPLATES_COLUMN_KEYS.LBS_LOCATION,
  TEMPLATES_COLUMN_KEYS.LOCATION_DETAILS,
  TEMPLATES_COLUMN_KEYS.ASSIGNED_TO,
  TEMPLATES_COLUMN_KEYS.DUE_DATE,
  TEMPLATES_COLUMN_KEYS.OWNER,
  TEMPLATES_COLUMN_KEYS.ROOT_CAUSE,
  TEMPLATES_COLUMN_KEYS.CREATED_BY,
  TEMPLATES_COLUMN_KEYS.CREATED_AT,
  TEMPLATES_COLUMN_KEYS.UPDATED_BY,
  TEMPLATES_COLUMN_KEYS.UPDATED_AT,
  TEMPLATES_COLUMN_KEYS.IS_ACTIVE,
  TEMPLATES_COLUMN_KEYS.CUSTOM_ATTRIBUTES
];

TemplatesTable.HiddenColumns = [
  TEMPLATES_COLUMN_KEYS.STATUS,
  TEMPLATES_COLUMN_KEYS.DESCRIPTION,
  TEMPLATES_COLUMN_KEYS.LBS_LOCATION,
  TEMPLATES_COLUMN_KEYS.LOCATION_DETAILS,
  TEMPLATES_COLUMN_KEYS.OWNER,
  TEMPLATES_COLUMN_KEYS.ROOT_CAUSE,
  TEMPLATES_COLUMN_KEYS.CREATED_AT,
  TEMPLATES_COLUMN_KEYS.UPDATED_BY,
  TEMPLATES_COLUMN_KEYS.UPDATED_AT
];

const TEMPLATES_COLUMN_GROUPS = {
  STANDARD: 'STANDARD',
  CUSTOM_ATTRIBUTES: 'CUSTOM_ATTRIBUTES'
};

TemplatesTable.displayName = 'TemplatesTable';

TemplatesTable.defaultProps = {
  locationsTreeNodes: [],
  projectId: '',
  analyticsId: ''
};

TemplatesTable.propTypes = {
  templateCategoriesEntitiesMap: templateCategoriesEntitiesMapType.isRequired,
  isTemplateCategoriesCollectionLoading: PropTypes.bool.isRequired,
  locationsTreeNodes: locationsTreeNodesType,
  isLocationsLoadingError: PropTypes.bool,
  isLocationsTreeLoading: PropTypes.bool.isRequired,
  projectId: PropTypes.string,
  analyticsId: PropTypes.string,
  oxygen: oxygenType.isRequired,
  currentUser: oxygenUserType.isRequired,
  rootCauseEntitiesMap: PropTypes.object.isRequired,
  pagination: paginationV2PropType.isRequired,
  didFetchOnce: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  tableData: templatesCollectionType.isRequired,
  highlightRowIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  customAttributes: PropTypes.arrayOf(PropTypes.object).isRequired,
  queryParams: templatesQueryParamsType.isRequired,
  setDuplicateTemplateIdAction: PropTypes.func.isRequired,
  setDeleteTemplateIdAction: PropTypes.func.isRequired,
  setQueryParamsAction: PropTypes.func.isRequired,
  changeLocationAction: PropTypes.func.isRequired,
  updateTemplateAction: PropTypes.func.isRequired,
  showNotificationAction: PropTypes.func.isRequired,
  issueTypeAdminEntitiesMap: PropTypes.object.isRequired
};

export default TemplatesTable;
